// ============================================================================
// CYBERGRID ASSAULT - SHARED TYPE DEFINITIONS
// ============================================================================

// ----------------------------------------------------------------------------
// CORE PRIMITIVES
// ----------------------------------------------------------------------------

export interface Vector2 {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Circle {
  x: number;
  y: number;
  radius: number;
}

// ----------------------------------------------------------------------------
// ENTITY SYSTEM
// ----------------------------------------------------------------------------

export type EntityId = string;

export interface Entity {
  id: EntityId;
  type: EntityType;
  position: Vector2;
  rotation: number; // radians
  active: boolean;
}

export enum EntityType {
  Player = 'player',
  Projectile = 'projectile',
  Pickup = 'pickup',
  Trigger = 'trigger',
  Prop = 'prop',
  Drone = 'drone',
}

// ----------------------------------------------------------------------------
// PLAYER & HERO
// ----------------------------------------------------------------------------

export enum HeroId {
  // Original heroes (keeping for backward compatibility, mapped to new roles)
  Spectre = 'spectre',   // Recon role
  Byte = 'byte',         // Tech/Cipher role  
  Vanta = 'vanta',       // Tank/Titan role
  Katana = 'katana',     // Assassin/Phantom role
  Glyph = 'glyph',       // Vision/Utility
  Spark = 'spark',       // Area Denial/Havoc role
  Zero = 'zero',         // Sniper
  Mira = 'mira',         // Medic/Support role
  
  // New hero aliases (for clarity in ability implementation)
  Phantom = 'katana',    // Assassin - blink, invisibility
  Titan = 'vanta',       // Tank - fortify, shockwave
  Medic = 'mira',        // Support - heal, revive
  Havoc = 'spark',       // Assault - grenade, minigun
  Cipher = 'byte',       // Tech - hack, turret
  Venom = 'glyph',       // Saboteur - poison (repurposing Glyph)
  Aegis = 'zero',        // Guardian - barriers (repurposing Zero)
}

export enum Team {
  None = 0,
  Attackers = 1,
  Defenders = 2,
}

export interface PlayerState {
  id: EntityId;
  name: string;
  peerId: string;
  team: Team;
  heroId: HeroId;
  position: Vector2;
  velocity: Vector2;
  rotation: number;
  health: number;
  maxHealth: number;
  shield: number;
  maxShield: number;
  alive: boolean;
  credits: number;
  kills: number;
  deaths: number;
  assists: number;
  grenadeCount: number;
  bombCallCount: number;
  missileStrikeCount: number;
  weapon: WeaponState;
  weaponInventory: (WeaponState | null)[];
  currentWeaponSlot: number;
  abilities: AbilityState[];
  input: PlayerInput;
}

export interface PlayerInput {
  moveX: number; // -1 to 1
  moveY: number; // -1 to 1
  aimX: number;  // world position
  aimY: number;  // world position
  shoot: boolean;
  reload: boolean;
  ability1: boolean;
  ability2: boolean;
  ultimate: boolean;
  interact: boolean;
  throwGrenade: boolean;
  useBombCall: boolean;
  useMissileStrike: boolean;
  weaponSlot: number;  // 0 = no switch, 1-10 = weapon slot
  sequence: number; // for input ordering
}

// ----------------------------------------------------------------------------
// WEAPONS
// ----------------------------------------------------------------------------

export enum WeaponId {
  BurstPistol = 'burst_pistol',
  NeoSMG9 = 'neo_smg9',
  PulseRifle = 'pulse_rifle',
  ScatterShot = 'scatter_shot',
  RailSniper = 'rail_sniper',
  ShardLauncher = 'shard_launcher',
  NanoRepeater = 'nano_repeater',
  ArcWelder = 'arc_welder',
  PhaseRepeater = 'phase_repeater',
  PlasmaCannon = 'plasma_cannon',
}

export interface WeaponState {
  weaponId: WeaponId;
  ammo: number;
  maxAmmo: number;
  reloading: boolean;
  reloadEndTime: number;
  lastFireTime: number;
}

export interface WeaponDefinition {
  id: WeaponId;
  name: string;
  price: number;
  damage: number;
  spread: number;         // radians
  fireRate: number;       // shots per second
  range: number;          // max distance
  ammoCapacity: number;
  reloadTime: number;     // ms
  burstCount: number;     // 1 = single shot
  burstDelay: number;     // ms between burst shots
  projectileSpeed: number; // 0 = hitscan
  headshotMultiplier: number;
}

// ----------------------------------------------------------------------------
// ABILITIES
// ----------------------------------------------------------------------------

export interface AbilityState {
  abilityIndex: number; // 0 = passive, 1 = skill1, 2 = skill2, 3 = ultimate
  cooldownEndTime: number;
  charges: number;
  active: boolean;
  duration: number;
  ultimateCharge: number; // Points accumulated toward ultimate
}

export interface AbilityDefinition {
  name: string;
  description: string;
  cooldown: number;      // ms
  duration: number;      // ms (0 = instant)
  charges: number;       // max charges
  chargeTime: number;    // ms to restore one charge
  isUltimate: boolean;
  ultimateCost: number;  // points needed
}

// ----------------------------------------------------------------------------
// GAME STATE
// ----------------------------------------------------------------------------

export enum GamePhase {
  Lobby = 'lobby',
  HeroSelect = 'hero_select',
  BuyPhase = 'buy_phase',
  RoundActive = 'round_active',
  RoundEnd = 'round_end',
  MatchEnd = 'match_end',
}

export enum GameMode {
  DataSpikeAssault = 'data_spike',
  ReactorEscort = 'reactor_escort',
  NeonDeathmatch = 'deathmatch',
}

export interface GameState {
  phase: GamePhase;
  mode: GameMode;
  mapId: string;
  tick: number;
  time: number;           // absolute game time in ms
  roundStartTime: number; // when current round started (for timer calc)
  roundNumber: number;
  maxRounds: number;
  roundTimeLimit: number; // ms
  attackerScore: number;
  defenderScore: number;
  players: Map<EntityId, PlayerState>;
  projectiles: Map<EntityId, ProjectileState>;
  objectives: ObjectiveState;
  // Recent shots for bullet tracer visualization on clients
  recentShots?: Array<{
    start: { x: number; y: number };
    end: { x: number; y: number };
    shooterId: EntityId;
    shooterPeerId: string;
  }>;
  // Countdown between rounds
  countdownTime?: number;  // Remaining countdown in ms (10000 = 10 seconds)
  countdownPaused?: boolean;
  countdownPausedBy?: string; // Player name who paused
  // Supply drop system
  aircraft?: Map<EntityId, import('../game/entities/Aircraft').AircraftState>;
  supplyCrates?: Map<EntityId, import('../game/entities/SupplyCrate').SupplyCrateState>;
  bombs?: Map<EntityId, import('../game/entities/Bomb').BombState>;
  grenades?: Map<EntityId, import('../game/entities/Grenade').GrenadeState>;
}

export interface ProjectileState {
  id: EntityId;
  ownerId: EntityId;
  position: Vector2;
  velocity: Vector2;
  damage: number;
  lifetime: number;
  spawnTime: number;
}

export interface ObjectiveState {
  // Data Spike
  spikeCarrierId: EntityId | null;
  spikePlanted: boolean;
  spikeSite: number; // 0, 1, etc. (-1 = not planted)
  spikePlantTime: number;
  spikeProgress: number; // 0-1 (upload progress after planted)
  spikePosition: Vector2 | null; // Position where spike is planted
  
  // Planting/Defusing progress
  plantingPlayerId: EntityId | null;
  plantingPeerId: string | null; // PeerId for network sync (entity IDs differ between host/client)
  plantingProgress: number; // 0-1
  defusingPlayerId: EntityId | null;
  defusingPeerId: string | null; // PeerId for network sync (entity IDs differ between host/client)
  defusingProgress: number; // 0-1

  // Reactor Escort
  reactorPosition: Vector2;
  reactorProgress: number; // 0-1
  reactorContested: boolean;
  reactorPushers: number;
}

// ----------------------------------------------------------------------------
// NETWORKING
// ----------------------------------------------------------------------------

export enum PacketType {
  // Connection
  Handshake = 0,
  HandshakeAck = 1,
  Disconnect = 2,
  Ping = 3,
  Pong = 4,

  // Lobby
  LobbyState = 10,
  PlayerJoin = 11,
  PlayerLeave = 12,
  PlayerReady = 13,
  TeamChange = 14,
  HeroSelect = 15,
  GameStart = 16,

  // Gameplay
  Input = 20,
  StateSnapshot = 21,
  StateDelta = 22,
  Event = 23,

  // Events
  PlayerDamage = 30,
  PlayerKill = 31,
  AbilityUse = 32,
  WeaponFire = 33,
  Reload = 34,
  SpikePlant = 35,
  SpikeDefuse = 36,
  RoundEnd = 37,

  // Countdown control
  CountdownPause = 40,
  CountdownResume = 41,

  // Game pause (tab focus)
  GamePause = 50,
  GameResume = 51,
}

export interface Packet {
  type: PacketType;
  timestamp: number;
  senderId: string;
  data: unknown;
}

export interface InputPacket extends Packet {
  type: PacketType.Input;
  data: PlayerInput;
}

export interface StateSnapshotPacket extends Packet {
  type: PacketType.StateSnapshot;
  data: {
    tick: number;
    players: PlayerState[];
    projectiles: ProjectileState[];
    objectives: ObjectiveState;
  };
}

export interface EventPacket extends Packet {
  type: PacketType.Event;
  data: GameEvent;
}

// ----------------------------------------------------------------------------
// GAME EVENTS
// ----------------------------------------------------------------------------

export enum GameEventType {
  PlayerDamage = 'player_damage',
  PlayerKill = 'player_kill',
  PlayerRespawn = 'player_respawn',
  AbilityUsed = 'ability_used',
  WeaponFired = 'weapon_fired',
  SpikePlanted = 'spike_planted',
  SpikeDefused = 'spike_defused',
  RoundStarted = 'round_started',
  RoundEnded = 'round_ended',
}

export interface GameEvent {
  type: GameEventType;
  tick: number;
  data: unknown;
}

export interface DamageEvent extends GameEvent {
  type: GameEventType.PlayerDamage;
  data: {
    targetId: EntityId;
    attackerId: EntityId;
    damage: number;
    position: Vector2;
    isHeadshot: boolean;
  };
}

export interface KillEvent extends GameEvent {
  type: GameEventType.PlayerKill;
  data: {
    victimId: EntityId;
    killerId: EntityId;
    weaponId: WeaponId;
    isHeadshot: boolean;
  };
}

// ----------------------------------------------------------------------------
// MAP DATA
// ----------------------------------------------------------------------------

export interface MapData {
  id: string;
  name: string;
  width: number;
  height: number;
  tileSize: number;
  tiles: number[][];          // collision layer
  spawnPoints: SpawnPoint[];
  spikeSites: Rectangle[];
  reactorPath: Vector2[];
  props: PropData[];
}

export interface SpawnPoint {
  position: Vector2;
  team: Team;
  index: number;
}

export interface PropData {
  id: string;
  type: string;
  position: Vector2;
  rotation: number;
  collider?: Rectangle;
}

// ----------------------------------------------------------------------------
// CONFIGURATION
// ----------------------------------------------------------------------------

export interface GameConfig {
  // Display
  canvasWidth: number;
  canvasHeight: number;
  targetFPS: number;

  // Simulation
  tickRate: number;           // server ticks per second
  snapshotRate: number;       // snapshots per second
  inputSendRate: number;      // inputs per second

  // Gameplay
  roundTimeLimit: number;     // ms
  buyPhaseTime: number;       // ms
  respawnTime: number;        // ms
  spikeUploadTime: number;    // ms
  spikePlantTime: number;     // ms
  spikeDefuseTime: number;    // ms

  // Movement
  playerSpeed: number;        // units per second
  playerAcceleration: number;
  playerFriction: number;

  // Combat
  headshotMultiplier: number;
  friendlyFire: boolean;
}

// Default config
export const DEFAULT_CONFIG: GameConfig = {
  canvasWidth: 1280,
  canvasHeight: 720,
  targetFPS: 60,

  tickRate: 20,
  snapshotRate: 10,
  inputSendRate: 20,

  roundTimeLimit: 180000,     // 3 minutes
  buyPhaseTime: 30000,        // 30 seconds
  respawnTime: 5000,
  spikeUploadTime: 45000,
  spikePlantTime: 3000,
  spikeDefuseTime: 5000,

  playerSpeed: 200,
  playerAcceleration: 1500,
  playerFriction: 800,

  headshotMultiplier: 2.0,
  friendlyFire: false,
};
